import type { GlossaryEntry } from "../types";

export const GLOSSARY: GlossaryEntry[] = [
  {
    term: "1099-MISC",
    description:
      "IRS form for reporting certain types of miscellaneous income, such as rents, prizes, awards, and other payments not reported on a W‑2.",
    externalUrl: "https://www.irs.gov/forms-pubs/about-form-1099-misc",
  },
  {
    term: "1099-NEC",
    description:
      "IRS form used to report nonemployee compensation, typically for independent contractors and freelancers, when payments total $600 or more in a tax year.",
    externalUrl: "https://www.irs.gov/forms-pubs/about-form-1099-nec",
  },
  {
    term: "Texas Franchise Tax",
    description:
      "A tax imposed by the State of Texas on certain business entities for the privilege of doing business in the state, based on revenue or margin calculations.",
    externalUrl: "https://comptroller.texas.gov/taxes/franchise/",
  },
  {
    term: "Severance Tax",
    description:
      "A tax levied on the extraction of natural resources, such as oil and gas, to compensate the state for the depletion of nonrenewable resources.",
    externalUrl: "https://comptroller.texas.gov/taxes/natural-gas/",
  },
  {
    term: "MACRS",
    description:
      "Modified Accelerated Cost Recovery System — the primary depreciation method used for tax purposes in the United States, allowing accelerated write-offs of asset costs.",
    externalUrl:
      "https://www.irs.gov/publications/p946#en_US_2023_publink100010754",
  },
  {
    term: "ADS",
    description:
      "Alternative Depreciation System — a depreciation method required for certain property under U.S. tax rules, using a longer recovery period and straight-line depreciation.",
    externalUrl:
      "https://www.irs.gov/publications/p946#en_US_2023_publink100010758",
  },
  {
    term: "Cost Depletion",
    description:
      "A method of calculating depletion for mineral property based on the property's adjusted basis and the proportion of the resource extracted during the year.",
    externalUrl: "https://www.irs.gov/taxtopics/tc703",
  },
  {
    term: "Percentage Depletion",
    description:
      "A method of calculating depletion for mineral property as a fixed percentage of gross income from production, subject to IRS limits and rules.",
    externalUrl: "https://www.irs.gov/taxtopics/tc703",
  },
  {
    term: "Intangible Drilling Costs",
    description:
      "Costs related to drilling wells for oil and gas that have no salvage value, such as labor, site preparation, and drilling fluids, which can often be deducted for tax purposes.",
    externalUrl: "https://www.irs.gov/irm/part4/irm_04-041-001",
  },
  {
    term: "Working Interest",
    description:
      "An ownership stake in an oil or gas well that entitles the holder to a share of production revenue, but also requires them to share in the costs of exploration, drilling, and operations.",
  },
  {
    term: "Royalty Interest",
    description:
      "An ownership interest in mineral rights that entitles the holder to a portion of production revenue without being responsible for any of the operational costs.",
  },
  {
    term: "Override Interest",
    description:
      "Overriding Royalty Interest — a share of production revenue granted to a party, usually carved out of the working interest, that ends when the underlying lease expires.",
  },
  {
    term: "Joint Interest Billing",
    description:
      "The process of allocating and invoicing costs among multiple working interest owners in a jointly operated oil or gas well.",
  },
  {
    term: "Joint Venture",
    description:
      "A business arrangement in which two or more parties combine resources for a specific project, sharing profits, losses, and control according to agreed terms.",
  },
  {
    term: "Multi-Entity Tax Planning",
    description:
      "Coordinating tax strategies across multiple related business entities to minimize overall tax liability and improve operational efficiency.",
  },
  {
    term: "Partnership Accounting",
    description:
      "The process of recording and managing the financial activities of a partnership, including capital contributions, profit allocations, and partner distributions.",
  },
  {
    term: "Leasehold Costs",
    description:
      "Expenses incurred to acquire and maintain the rights to explore for and produce oil or gas from a leased property, including lease bonuses, rentals, and legal fees.",
  },
  {
    term: "Depreciation",
    description:
      "The process of allocating the cost of a tangible asset over its useful life, reflecting wear, tear, and obsolescence in financial statements and tax reporting.",
    externalUrl: "https://www.irs.gov/publications/p946",
  },
  {
    term: "Amortization",
    description:
      "The systematic write-off of an intangible asset's cost over its useful life, often applied to leasehold improvements, patents, and certain deferred costs.",
    externalUrl:
      "https://www.irs.gov/publications/p535#en_US_2023_publink100019894",
  },
  {
    term: "Revenue Allocation",
    description:
      "The process of distributing revenue among stakeholders or accounts based on ownership percentages, agreements, or contractual obligations.",
  },
  {
    term: "Production Volumes",
    description:
      "The measured quantity of oil, gas, or other hydrocarbons extracted from a well or field over a given period, used for revenue calculation and reporting.",
  },
  {
    term: "Development Costs",
    description:
      "Expenses related to preparing a proven oil or gas property for production, including drilling development wells, building facilities, and installing equipment.",
  },
  {
    term: "Mineral Rights",
    description:
      "The legal rights to explore for, extract, and sell minerals found beneath a tract of land, which can be sold or leased separately from surface rights.",
  },
  {
    term: "Inherited Mineral Rights",
    description:
      "Mineral rights received through inheritance, which may require valuation, title transfer, and tax considerations for heirs or beneficiaries.",
  },
  {
    term: "Basis Tracking",
    description:
      "The process of recording and maintaining the adjusted cost basis of an asset, used to determine gain, loss, or depletion deductions when the asset is sold or depleted.",
  },
  {
    term: "Energy Trusts",
    description:
      "A legal arrangement that holds and manages oil, gas, or mineral assets for the benefit of designated beneficiaries, often used for estate planning and tax management.",
  },
  {
    term: "Succession Planning",
    description:
      "The process of preparing for the transfer of ownership, management, and control of business or investment assets to the next generation or designated successors.",
  },
  {
    term: "Tax Planning",
    description:
      "A proactive approach to managing your tax obligations throughout the year to minimize liabilities, maximize deductions, and align with your broader financial goals.",
    internalHref: "/services/tax/tax-planning",
  },
  {
    term: "Tax Compliance",
    description:
      "Ensuring your business meets all federal, state, and local tax filing requirements accurately and on time, including income, payroll, and information returns.",
    internalHref: "/services/tax/tax-compliance",
  },
  {
    term: "Policies & Procedures",
    description:
      "Documented guidelines that establish consistent standards for financial operations, helping your business stay organized, compliant, and audit-ready.",
    internalHref: "/services/accounting/policies-and-procedures",
  },
  {
    term: "State Franchise Income & State Return",
    description:
      "Preparation and filing of state-level franchise tax and income tax returns to keep your business compliant with jurisdictional requirements.",
    internalHref: "/services/tax/state-franchise-income-and-state-return",
  },
  {
    term: "Succession Planning & Wealth Transfer",
    description:
      "Strategies for passing business or investment assets to the next generation or chosen successors while minimizing tax impact and preserving wealth.",
    internalHref: "/services/advisory/succession-planning-and-wealth-transfer",
  },
];

export const GLOSSARY_BY_KEY = Object.fromEntries(
  GLOSSARY.map(glossary => [glossary.term, glossary]),
);
